// Developer-Komponente: Helm-Bilder pro Wache verwalten.
// Erlaubt Upload eines eigenen Helm-Bilds pro Kategorie (standard/hbm/hbmz/gehob1/gehob2).
// Fallback bleibt das mitgelieferte Default-Helm-Bild.
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { HardHat, Upload, RotateCcw, Loader2 } from 'lucide-react';
import { developerApi } from '../../lib/api';
import { DEFAULT_HELMETS } from '../../lib/dienstgrade';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const KATEGORIEN = [
  { key: 'standard', label: 'Standard', sub: 'BM, OBM, BM-A' },
  { key: 'hbm',      label: 'HBM',      sub: 'Hauptbrandmeister' },
  { key: 'hbmz',     label: 'HBMz',     sub: 'Hauptbrandmeister m.Z.' },
  { key: 'gehob1',   label: 'Gehoben 1', sub: 'BI – BOAR' },
  { key: 'gehob2',   label: 'Gehoben 2', sub: 'BR aufwärts / Höherer Dienst' },
];

export function HelmeSection() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await developerApi.getHelme();
      setConfig(res.data?.helme || {});
    } catch (e) {
      console.error(e);
      toast.error('Helm-Konfiguration konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (kategorie, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5 MB)');
      return;
    }
    setUploading(s => ({ ...s, [kategorie]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      await developerApi.uploadHelm(kategorie, fd);
      toast.success(`${kategorie}-Helm aktualisiert`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(s => ({ ...s, [kategorie]: false }));
    }
  };

  const handleReset = async (kategorie) => {
    try {
      await developerApi.resetHelm(kategorie);
      toast.success(`${kategorie}-Helm auf Standard zurückgesetzt`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Zurücksetzen fehlgeschlagen');
    }
  };

  return (
    <Card data-testid="developer-helme-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardHat className="w-5 h-5" />
          Diensthelme dieser Wache
        </CardTitle>
        <CardDescription>
          Pro Kategorie ein eigenes Helm-Bild hochladen. Wird im Dashboard für jeden
          Mitarbeiter dieser Wache je nach Dienstgrad angezeigt. Ohne eigenes Bild
          wird der Standard-Helm angezeigt.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {KATEGORIEN.map(kat => {
            const customUrl = config[kat.key];
            const previewUrl = customUrl || DEFAULT_HELMETS[kat.key];
            const isCustom = !!customUrl;
            const isUploading = !!uploading[kat.key];
            return (
              <div
                key={kat.key}
                className="rounded-lg border p-4 flex flex-col gap-3 bg-card"
                data-testid={`helm-card-${kat.key}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-sm">{kat.label}</div>
                    <div className="text-[11px] text-muted-foreground">{kat.sub}</div>
                  </div>
                  {isCustom ? (
                    <Badge variant="default" className="text-[10px]">Custom</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">Standard</Badge>
                  )}
                </div>
                <div className="flex items-center justify-center h-32 bg-slate-50 rounded-md overflow-hidden">
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  ) : (
                    <img
                      src={previewUrl}
                      alt={kat.label}
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => { e.target.src = DEFAULT_HELMETS[kat.key]; }}
                    />
                  )}
                </div>
                <input
                  ref={el => (fileRefs.current[kat.key] = el)}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid={`helm-file-input-${kat.key}`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    handleUpload(kat.key, file);
                    e.target.value = '';
                  }}
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1"
                    disabled={isUploading}
                    onClick={() => fileRefs.current[kat.key]?.click()}
                    data-testid={`helm-upload-btn-${kat.key}`}
                  >
                    {isUploading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    {isCustom ? 'Ersetzen' : 'Hochladen'}
                  </Button>
                  {isCustom && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReset(kat.key)}
                      data-testid={`helm-reset-btn-${kat.key}`}
                      title="Auf Standard zurücksetzen"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Max. 5 MB pro Bild. Formate: PNG, JPEG, WebP. Empfohlen: WebP mit transparentem Hintergrund.
        </p>
      </CardContent>
    </Card>
  );
}

export default HelmeSection;
